/**
 * A WeaveIn meeting's calendar entry (TRUS-694).
 *
 * The meeting (TRUS-691) is the source of truth, so its calendar entry is
 * read-only here: the real title and invitees for people who may see the
 * meeting (host, attendees, channel readers, org admins - decided by the
 * server), the generic "Meeting with <org>" title for everyone else. The video
 * provider is always "WeaveIn meeting"; edits and cancellation happen on the
 * meeting card, which this panel links to.
 */

import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { Calendar, Clock, ExternalLink, Lock, Users, Video, X } from 'lucide-react';
import type { CalendarEvent, CalendarMeetingInvitee } from '@/services/api/calendar';
import { useAppTranslation } from '@/contexts/TranslationContext';

interface MeetingEventPanelProps {
  event: CalendarEvent;
  onClose: () => void;
}

function rsvpLabel(t: (k: string, d: string) => string, rsvp: CalendarMeetingInvitee['rsvp']): string {
  switch (rsvp) {
    case 'accepted':
      return t('calendar.meetingEvent.rsvpAccepted', 'Accepted');
    case 'declined':
      return t('calendar.meetingEvent.rsvpDeclined', 'Declined');
    case 'tentative':
      return t('calendar.meetingEvent.rsvpTentative', 'Maybe');
    default:
      return t('calendar.meetingEvent.rsvpPending', 'No answer yet');
  }
}

export default function MeetingEventPanel({ event, onClose }: MeetingEventPanelProps) {
  const { t } = useAppTranslation();
  // TRUS-695: shown in the viewer's selected zone, with its abbreviation.
  const tz = useUserTimezone();
  const meeting = event.meeting!;
  const visible = meeting.visible;
  const invitees = meeting.invitees ?? [];
  const start = event.start_time ? new Date(event.start_time) : null;
  const end = event.end_time ? new Date(event.end_time) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="meeting-event-panel">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-teal-700 dark:text-teal-300">
              {t('calendar.meetingEvent.kind', 'WeaveIn meeting')}
            </p>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white break-words" data-testid="meeting-event-title">
              {event.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('calendar.meetingEvent.close', 'Close')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {start && (
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <Calendar className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <span>{formatInTimeZone(start, tz, 'EEE, MMM d, yyyy')}</span>
              <Clock className="h-4 w-4 flex-shrink-0 ml-2" aria-hidden="true" />
              <span>
                {formatInTimeZone(start, tz, 'h:mm a')}
                {end ? ` – ${formatInTimeZone(end, tz, 'h:mm a')}` : ''} {formatInTimeZone(end ?? start, tz, 'zzz')}
              </span>
            </div>
          )}

          <div>
            <p className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('calendar.meetingEvent.providerLabel', 'Video conference provider')}
            </p>
            <div
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-200"
              data-testid="meeting-event-provider"
            >
              <Video className="h-4 w-4" aria-hidden="true" />
              {t('calendar.meetingEvent.provider', 'WeaveIn meeting')}
            </div>
          </div>

          {!visible ? (
            <div
              className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300"
              data-testid="meeting-event-private"
            >
              <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              {t(
                'calendar.meetingEvent.notInvited',
                "You aren't part of this meeting, so its details are private.",
              )}
            </div>
          ) : (
            <>
              {event.description && (
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{event.description}</p>
              )}

              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Users className="h-4 w-4" aria-hidden="true" />
                  {t('calendar.meetingEvent.invitees', 'Invitees')} ({invitees.length})
                </p>
                {invitees.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('calendar.meetingEvent.noInvitees', 'No one else is invited yet.')}
                  </p>
                ) : (
                  <ul className="space-y-1" data-testid="meeting-event-invitees">
                    {invitees.map((inv, i) => (
                      <li
                        key={`${inv.kind}-${i}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm dark:bg-gray-700/50"
                      >
                        <span className="min-w-0 truncate text-gray-900 dark:text-white">
                          {inv.name || inv.email}
                          {inv.kind === 'external' && inv.email && inv.name && (
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{inv.email}</span>
                          )}
                          {inv.kind === 'member' && (
                            <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                              {t('calendar.meetingEvent.member', 'Member')}
                            </span>
                          )}
                        </span>
                        <span className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                          {rsvpLabel(t, inv.rsvp)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {meeting.path && (
                <div className="flex flex-wrap items-center gap-3">
                  <a
                    href={meeting.path}
                    data-testid="meeting-event-open"
                    className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
                  >
                    {meeting.can_join_now ? (
                      <Video className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    )}
                    {meeting.can_join_now
                      ? t('calendar.meetingEvent.join', 'Join')
                      : t('calendar.meetingEvent.open', 'Open meeting')}
                  </a>
                  {meeting.can_edit && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('calendar.meetingEvent.editOnCard', 'Edit or cancel it from the meeting card.')}
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
